import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { productService } from '../../../services/productService';

const PersonalizedRecommendations = ({ title, subtitle }) => {
  const [activeTab, setActiveTab] = useState('recent');
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const recommendationTabs = [
    { id: 'recent', label: 'Recently Viewed', icon: 'Clock' },
    { id: 'similar', label: 'Similar Models', icon: 'Sparkles' },
    { id: 'trending', label: 'Trending in Your Category', icon: 'TrendingUp' }
  ];

  useEffect(() => {
    loadRecommendations();
  }, [activeTab]);

  const loadRecommendations = async () => {
    try {
      setLoading(true);
      const products = await productService?.getFeatured(4);
      
      // Map products to match the expected structure
      const mappedProducts = products?.map(product => ({
        id: product?.id,
        name: product?.name,
        creator: product?.manufacturerName || 'OpenGadget',
        creatorAvatar: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%2394a3b8"><circle cx="12" cy="8" r="4"/><path d="M12 14c-6.1 0-8 4-8 4v2h16v-2s-1.9-4-8-4z"/></svg>',
        creatorAvatarAlt: `${product?.manufacturerName || 'OpenGadget'} designer`,
        image: product?.thumbnailPath || "/assets/images/model-fallback.jpg",
        alt: product?.description || product?.name,
        price: Number(product?.price) || 0,
        originalPrice: null,
        rating: 4.8,
        reviews: Math.floor(Math.random() * 500) + 100,
        products: product?.soldCount || 0,
        category: product?.categoryName || "Products",
        isPro: product?.isFeatured || false,
        discount: null,
        matchScore: 95
      })) || [];

      setRecommendations(mappedProducts);
      setError('');
    } catch (err) {
      console.error('Error loading recommendations:', err);
      setError(err?.message || 'Failed to load recommendations');
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (e, productId) => {
    // Prevent navigation if clicking on action buttons
    const isActionButton = e?.target?.closest('button') || e?.target?.closest('a[href*="shopping-cart"]');
    if (isActionButton) {
      e?.stopPropagation();
      return;
    }
    navigate(`/3d-model-viewer?id=${productId}`);
  };

  const currentRecommendations = recommendations;

  if (loading) {
    return (
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-6">
          <div className="text-center">
            <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Icon name="Target" size={16} />
              <span>Loading...</span>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-6">
          <div className="text-center">
            <p className="text-destructive">{error}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-muted/30">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Icon name="Target" size={16} />
            <span>Personalized for You</span>
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            {title || 'Your Vision, Our Models'}
          </h2>
          
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            {subtitle || `Discover models tailored to your interests and browsing history.
            Find exactly what you need for your next creative project.`}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {recommendationTabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                activeTab === tab?.id
                  ? 'bg-primary text-primary-foreground shadow-soft'
                  : 'bg-card text-muted-foreground hover:text-foreground hover:bg-card/80'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </div>

        {/* Recommendations Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {currentRecommendations?.map((model) => (
            <div
              key={model?.id}
              className="group bg-card rounded-xl overflow-hidden shadow-soft hover:shadow-floating transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
              onClick={(e) => handleCardClick(e, model?.id)}
            >
              {/* Image Container */}
              <div className="relative aspect-[4/3] overflow-hidden">
                <Image
                  src={model?.image}
                  alt={model?.alt}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                
                {/* Badges */}
                <div className="absolute top-3 left-3 flex flex-col gap-2">
                  {model?.isPro && (
                    <span className="bg-accent text-accent-foreground px-2 py-1 rounded-md text-xs font-medium">
                      Pro Model
                    </span>
                  )}
                  {model?.discount && (
                    <span className="bg-success text-success-foreground px-2 py-1 rounded-md text-xs font-medium">
                      {model?.discount}% Off
                    </span>
                  )}
                  {activeTab === 'similar' && (
                    <span className="bg-success text-success-foreground px-2 py-1 rounded-md text-xs font-medium">
                      {model?.matchScore}% Match
                    </span>
                  )}
                </div>

                {/* Quick Actions */}
                <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="flex flex-col gap-2">
                    <Button variant="ghost" size="icon" className="bg-black/50 backdrop-blur-sm text-white hover:bg-black/70">
                      <Icon name="Heart" size={16} />
                    </Button>
                    <Link to="/3d-model-viewer">
                      <Button variant="ghost" size="icon" className="bg-black/50 backdrop-blur-sm text-white hover:bg-black/70">
                        <Icon name="Eye" size={16} />
                      </Button>
                    </Link>
                  </div>
                </div>

                {/* Price Tag */}
                <div className="absolute bottom-3 right-3 bg-primary text-primary-foreground px-3 py-1 rounded-lg text-sm font-semibold">
                  ${model?.price}
                </div>
              </div>

              {/* Content */}
              <div className="p-4">
                <div className="mb-2">
                  <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-1">
                    {model?.name}
                  </h3>
                  <p className="text-sm text-muted-foreground">{model?.category}</p>
                </div>

                {/* Stats */}
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
                  <div className="flex items-center space-x-1">
                    <Icon name="ShoppingBag" size={12} />
                    <span>{model?.products?.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="MessageCircle" size={12} />
                    <span>{model?.reviews}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-1">
                    <Icon name="Star" size={14} color="var(--color-accent)" />
                    <span className="text-sm font-medium">{model?.rating}</span>
                  </div>
                  <div className="flex items-center space-x-1 text-muted-foreground">
                    <Icon name="Download" size={14} />
                    <span className="text-sm">{model?.products?.toLocaleString()}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">by {model?.creator}</p>
                  {activeTab === 'recent' && (
                    <p className="text-xs text-muted-foreground">{model?.lastViewed}</p>
                  )}
                </div>

                {/* Add to Cart Button */}
                <div className="mt-4">
                  <Link to="/shopping-cart">
                    <Button variant="outline" size="sm" fullWidth iconName="ShoppingCart" iconPosition="left">
                      Add to Cart
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* View More Button */}
        <div className="text-center">
          <Link to="/model-catalog">
            <Button variant="default" size="lg" iconName="Grid" iconPosition="left">
              Browse All Models
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default PersonalizedRecommendations;